import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
} from '@mui/material';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import ArticleIcon from '@mui/icons-material/Article';
import LaunchIcon from '@mui/icons-material/Launch';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import { useArticles } from '../hooks/useContent';

const Blogs: React.FC = () => {
  const { articles } = useArticles();

  return (
    <main
      style={{
        position: 'relative',
        zIndex: 1,
        minHeight: 'calc(100vh - 64px)',
        paddingTop: 48,
        paddingBottom: 80,
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{ marginBottom: 40 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: '1.4rem' }}>📝</span>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.8rem', sm: '2.4rem' },
                background: 'linear-gradient(135deg, #EFC430 0%, #F4D675 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Notes & Technical Articles
            </Typography>
          </div>

          <Typography variant="body1" sx={{ color: '#9CA3AF', mt: 1, maxWidth: 640, lineHeight: 1.6 }}>
            Technical notes, homelab architectures, and engineering writeups. Available as markdown articles, PDF documents, or external publications.
          </Typography>
        </motion.div>

        {/* Blog Posts Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: 24,
          }}
        >
          {articles.map((post) => {
            const isMarkdown = post.format === 'markdown';
            const isPdf = post.format === 'pdf';
            const isExternal = post.format === 'external';

            return (
              <Card
                key={post.slug}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  backgroundColor: 'rgba(20, 20, 22, 0.65)',
                  backdropFilter: 'blur(16px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: '18px',
                  transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: 'rgba(239, 196, 48, 0.35)',
                    boxShadow: '0 12px 32px rgba(239, 196, 48, 0.08)',
                  },
                }}
              >
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 14,
                      color: '#9CA3AF',
                      fontSize: '0.75rem',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {isPdf ? (
                        <PictureAsPdfIcon sx={{ fontSize: 16, color: '#F43F5E' }} />
                      ) : (
                        <ArticleIcon sx={{ fontSize: 16, color: '#EFC430' }} />
                      )}
                      <span
                        style={{
                          textTransform: 'uppercase',
                          fontWeight: 600,
                          color: isPdf ? '#F43F5E' : '#EFC430',
                        }}
                      >
                        {post.format}
                      </span>
                      {post.read_time && <span>• {post.read_time}</span>}
                    </div>
                  </div>

                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                      color: '#FFFFFF',
                      fontWeight: 700,
                      fontSize: '1.15rem',
                      lineHeight: 1.35,
                      mb: 1.5,
                    }}
                  >
                    {post.title}
                  </Typography>

                  {post.excerpt && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#9CA3AF',
                        fontSize: '0.85rem',
                        lineHeight: 1.6,
                        mb: 2.5,
                      }}
                    >
                      {post.excerpt}
                    </Typography>
                  )}

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {post.tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.04)',
                          border: '1px solid rgba(255, 255, 255, 0.06)',
                          color: '#D1D5DB',
                          fontSize: '0.72rem',
                          height: 22,
                        }}
                      />
                    ))}
                  </div>
                </CardContent>

                <CardActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
                  {isMarkdown && (
                    <Button
                      component={RouterLink}
                      to={`/blog/${post.slug}`}
                      size="small"
                      endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                      sx={{
                        color: '#EFC430',
                        fontWeight: 600,
                        fontSize: '0.82rem',
                        textTransform: 'none',
                        p: 0,
                        '&:hover': { backgroundColor: 'transparent', color: '#F4D675' },
                      }}
                    >
                      Read Full Note
                    </Button>
                  )}

                  {isPdf && post.file_url && (
                    <Button
                      size="small"
                      endIcon={<LaunchIcon sx={{ fontSize: 14 }} />}
                      onClick={() => window.open(post.file_url!, '_blank')}
                      sx={{
                        color: '#F43F5E',
                        fontWeight: 600,
                        fontSize: '0.82rem',
                        textTransform: 'none',
                        p: 0,
                        '&:hover': { backgroundColor: 'transparent', color: '#FDA4AF' },
                      }}
                    >
                      Read PDF ↗
                    </Button>
                  )}

                  {isExternal && post.file_url && (
                    <Button
                      size="small"
                      endIcon={<LaunchIcon sx={{ fontSize: 14 }} />}
                      onClick={() => window.open(post.file_url!, '_blank')}
                      sx={{
                        color: '#EFC430',
                        fontWeight: 600,
                        fontSize: '0.82rem',
                        textTransform: 'none',
                        p: 0,
                        '&:hover': { backgroundColor: 'transparent', color: '#F4D675' },
                      }}
                    >
                      Read on Publication ↗
                    </Button>
                  )}
                </CardActions>
              </Card>
            );
          })}
        </div>
      </Container>
    </main>
  );
};

export default Blogs;
